class ProductVisibilityManager {
  constructor() {
    this.HSA_VARIANT_ID = '47239926350049';
    this.SUBSCRIPTION_PRODUCT_ID = '9079699341537';
    this.HSA_PRODUCT_ID = '9078408446177';

    this.hsaVariantSelected = false;
    this.cartHasHSA = false;
    this.cartHasSubscription = false;
    this.cartItems = [];

    // Store bound handlers for cleanup
    this.boundHandlers = {
      handleVariantSelection: this.handleVariantSelection.bind(this),
      handleQuickAddVariantChange: this.handleQuickAddVariantChange.bind(this),
      applyVisibilityRules: this.applyVisibilityRules.bind(this),
      checkCartState: this.checkCartState.bind(this)
    };

    this.init();
  }

  init() {
    document.addEventListener('variant:selected', this.boundHandlers.handleVariantSelection);

    document.addEventListener('quickadd:variant:changed', this.boundHandlers.handleQuickAddVariantChange);

    this.checkInitialState();

    document.addEventListener('collection:updated', this.boundHandlers.applyVisibilityRules);

    this.checkCartState();

    document.addEventListener('cart:updated', this.boundHandlers.checkCartState);
  }

  destroy() {
    // Remove all event listeners to prevent memory leaks
    document.removeEventListener('variant:selected', this.boundHandlers.handleVariantSelection);
    document.removeEventListener('quickadd:variant:changed', this.boundHandlers.handleQuickAddVariantChange);
    document.removeEventListener('collection:updated', this.boundHandlers.applyVisibilityRules);
    document.removeEventListener('cart:updated', this.boundHandlers.checkCartState);
  }

  handleVariantSelection(event) {
    const { variantId, productId } = event.detail;

    if (String(productId) === String(this.HSA_PRODUCT_ID)) {
      this.updateHSAState(String(variantId));
    }
  }

  handleQuickAddVariantChange(event) {
    const { variantId, productId } = event.detail;

    if (String(productId) === String(this.HSA_PRODUCT_ID)) {
      this.updateHSAState(String(variantId));
    }
  }

  updateHSAState(variantId) {
    const wasHsaSelected = this.hsaVariantSelected;
    this.hsaVariantSelected = (String(variantId) === String(this.HSA_VARIANT_ID));

    if (wasHsaSelected !== this.hsaVariantSelected) {
      // console.log(`HSA variant ${this.hsaVariantSelected ? 'selected' : 'deselected'} (variant ID: ${variantId})`);
      this.applyVisibilityRules();
      this.storeState();
    }
  }

  applyVisibilityRules() {
    const subscriptionProduct = this.findProductElement(this.SUBSCRIPTION_PRODUCT_ID);

    if (subscriptionProduct) {
      if (this.hsaVariantSelected) {
        this.hideProduct(subscriptionProduct);
      } else {
        this.showProduct(subscriptionProduct);
      }
    }
  }

  findProductElement(productId) {
    const selectors = [
      `li[data-product-id="${productId}"]`,
      `.product-grid__item[data-product-id="${productId}"]`,
      `[data-product-id="${productId}"].product-grid__item`
    ];

    for (const selector of selectors) {
      const element = document.querySelector(selector);
      if (element) {
        // console.log(`Found product element for ID ${productId}:`, element);
        return element;
      }
    }

    // console.log(`Could not find product element for ID ${productId}`);
    return null;
  }

  hideProduct(element) {
    if (!element) return;

    element.classList.add('product-hiding');

    setTimeout(() => {
      element.style.display = 'none';
      element.classList.add('product-hidden-hsa');
      element.setAttribute('aria-hidden', 'true');
    }, 300);

    this.addHSANotice();
  }

  showProduct(element) {
    if (!element) return;

    element.style.display = '';
    element.classList.remove('product-hidden-hsa', 'product-hiding');
    element.removeAttribute('aria-hidden');

    this.removeHSANotice();
  }

  addHSANotice() {
    if (document.getElementById('hsa-restriction-notice')) return;

    const notice = document.createElement('div');
    notice.id = 'hsa-restriction-notice';
    notice.className = 'hsa-notice';
    notice.innerHTML = `
      <div class="hsa-notice__content">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <span>HSA/FSA eligible item selected - Subscription products are hidden as they cannot be purchased with HSA/FSA funds</span>
      </div>
    `;

    const collection = document.querySelector('.collection-wrapper, .product-grid-container');
    if (collection) {
      collection.insertBefore(notice, collection.firstChild);
    }
  }

  removeHSANotice() {
    const notice = document.getElementById('hsa-restriction-notice');
    if (notice) {
      notice.remove();
    }
  }

  checkInitialState() {
    this.hsaVariantSelected = false;

    try {
      sessionStorage.removeItem('hsaVariantSelected');
    } catch (e) {
      // Storage might be disabled or in private browsing mode
    }

    this.checkCurrentSelections();
  }

  checkCurrentSelections() {
    const variantSelects = document.querySelectorAll('.quick-add__variant-select');
    variantSelects.forEach(select => {
      const quickAddComponent = select.closest('quick-add-component');
      if (quickAddComponent && String(quickAddComponent.dataset.productId) === String(this.HSA_PRODUCT_ID)) {
        if (String(select.value) === String(this.HSA_VARIANT_ID)) {
          // console.log('HSA variant is already selected on page load');
          this.hsaVariantSelected = true;
          this.applyVisibilityRules();
        }
      }
    });
  }

  storeState() {
    try {
      sessionStorage.setItem('hsaVariantSelected', this.hsaVariantSelected.toString());
    } catch (e) {
      // Storage might be disabled or in private browsing mode
    }
  }

  async checkCartState() {
    try {
      // Add timeout to cart fetch
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout for cart check

      const response = await fetch('/cart.js', {
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();
      this.cartItems = cart.items || [];

      this.cartHasHSA = this.cartItems.some(item =>
        String(item.variant_id) === String(this.HSA_VARIANT_ID)
      );

      this.cartHasSubscription = this.cartItems.some(item =>
        String(item.product_id) === String(this.SUBSCRIPTION_PRODUCT_ID)
      );

      this.updateAddToCartButtons();
      this.updateCartPageNotices();

      const event = new CustomEvent('cart:conflict:checked', {
        detail: {
          hasHSA: this.cartHasHSA,
          hasSubscription: this.cartHasSubscription
        }
      });
      document.dispatchEvent(event);

    } catch (error) {
      // Handle different error types
      if (error.name === 'AbortError') {
        // Timeout - cart check took too long
        // console.error('Cart check timed out');
      } else if (error.message && error.message.includes('JSON')) {
        // JSON parse error
        // console.error('Invalid cart response');
      } else {
        // Network or other error
        // console.error('Error checking cart state:', error);
      }

      // Set safe defaults on error
      this.cartItems = [];
      this.cartHasHSA = false;
      this.cartHasSubscription = false;

      // Still dispatch event with safe defaults so UI can update
      const event = new CustomEvent('cart:conflict:checked', {
        detail: {
          hasHSA: false,
          hasSubscription: false,
          error: true
        }
      });
      document.dispatchEvent(event);
    }
  }

  updateAddToCartButtons() {
    if (this.cartHasHSA) {
      this.disableSubscriptionButtons();
    } else if (this.cartHasSubscription) {
      this.disableHSAButtons();
    } else {
      this.enableAllButtons();
    }
  }

  disableSubscriptionButtons() {
    const subscriptionForms = document.querySelectorAll(`[data-product-id="${this.SUBSCRIPTION_PRODUCT_ID}"] form`);
    subscriptionForms.forEach(form => {
      const button = form.querySelector('button[type="submit"]');
      if (button) {
        button.disabled = true;
        button.setAttribute('data-conflict-disabled', 'true');
        const textElement = button.querySelector('.add-to-cart-text__content');
        if (textElement) {
          textElement.setAttribute('data-original-text', textElement.textContent);
          textElement.textContent = 'Unavailable - HSA item in cart';
        }
      }
    });
  }

  disableHSAButtons() {
    const hsaSelectors = document.querySelectorAll('.quick-add__variant-select');
    hsaSelectors.forEach(select => {
      const quickAddComponent = select.closest('quick-add-component');
      if (quickAddComponent && String(quickAddComponent.dataset.productId) === String(this.HSA_PRODUCT_ID)) {
        const form = quickAddComponent.querySelector('form');
        if (form && String(select.value) === String(this.HSA_VARIANT_ID)) {
          const button = form.querySelector('button[type="submit"]');
          if (button) {
            button.disabled = true;
            button.setAttribute('data-conflict-disabled', 'true');
            const textElement = button.querySelector('.add-to-cart-text__content');
            if (textElement) {
              textElement.setAttribute('data-original-text', textElement.textContent);
              textElement.textContent = 'Unavailable - Subscription in cart';
            }
          }
        }
      }
    });
  }

  enableAllButtons() {
    const disabledButtons = document.querySelectorAll('button[data-conflict-disabled="true"]');
    disabledButtons.forEach(button => {
      button.disabled = false;
      button.removeAttribute('data-conflict-disabled');
      const textElement = button.querySelector('.add-to-cart-text__content');
      if (textElement && textElement.hasAttribute('data-original-text')) {
        textElement.textContent = textElement.getAttribute('data-original-text');
        textElement.removeAttribute('data-original-text');
      }
    });
  }

  hasConflict(productId, variantId) {
    if (String(productId) === String(this.SUBSCRIPTION_PRODUCT_ID) && this.cartHasHSA) {
      return { hasConflict: true, message: 'Cannot add subscription products when HSA/FSA eligible items are in cart' };
    }

    if (String(variantId) === String(this.HSA_VARIANT_ID) && this.cartHasSubscription) {
      return { hasConflict: true, message: 'Cannot add HSA/FSA eligible items when subscription products are in cart' };
    }

    return { hasConflict: false, message: '' };
  }

  updateCartPageNotices() {
    const isCartPage = window.location.pathname.includes('/cart');
    const cartDrawer = document.querySelector('cart-drawer');
    const cartContainer = document.querySelector('.cart-items, .cart-drawer__items, cart-items-component');

    if (!isCartPage && !cartDrawer && !cartContainer) return;

    const existingNotice = document.getElementById('cart-conflict-notice');

    if (this.cartHasHSA) {
      if (!existingNotice) {
        const notice = document.createElement('div');
        notice.id = 'cart-conflict-notice';
        notice.className = 'cart-conflict-notice';
        notice.innerHTML = `
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
          </svg>
          <span>Note: HSA/FSA eligible items in cart. Subscription products cannot be added with HSA/FSA items.</span>
        `;

        if (cartContainer) {
          cartContainer.insertBefore(notice, cartContainer.firstChild);
        }
      }
    } else {
      if (existingNotice) {
        existingNotice.remove();
      }
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productVisibilityManager = new ProductVisibilityManager();
  });
} else {
  window.productVisibilityManager = new ProductVisibilityManager();
}

const style = document.createElement('style');
style.textContent = `
  .product-hiding {
    opacity: 0;
    transform: scale(0.95);
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  .product-hidden-hsa {
    display: none !important;
  }

  .hsa-notice {
    background: #FFF3CD;
    border: 1px solid #FFC107;
    border-radius: 4px;
    padding: 12px 16px;
    margin-bottom: 20px;
    color: #856404;
  }

  .hsa-notice__content {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
  }

  .hsa-notice svg {
    flex-shrink: 0;
  }

  button[data-conflict-disabled="true"] {
    background-color: #e0e0e0 !important;
    color: #666 !important;
    cursor: not-allowed !important;
    opacity: 0.7 !important;
  }

  button[data-conflict-disabled="true"]:hover {
    background-color: #e0e0e0 !important;
  }

  .cart-conflict-notice {
    background: #FFF3CD;
    border: 1px solid #FFC107;
    border-radius: 4px;
    padding: 12px 16px;
    margin: 16px 0;
    color: #856404;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .cart-conflict-notice svg {
    flex-shrink: 0;
  }
`;
document.head.appendChild(style);